import { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Mail, ArrowLeft } from 'lucide-react';

const AUTH_IMAGE = "https://images.unsplash.com/photo-1761083042210-bb334df182e6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkYXJrJTIwd29vZCUyMG9mZmljZSUyMGludGVyaW9yfGVufDB8fHx8MTc3MzY5NDY5MXww&ixlib=rb-4.1.0&q=85";

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!email) navigate('/register');
  }, [email, navigate]);

  useEffect(() => {
    if (cooldown > 0) {
      const t = setTimeout(() => setCooldown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [cooldown]);

  const handleChange = (idx, val) => {
    if (!/^\d?$/.test(val)) return;
    const next = [...digits];
    next[idx] = val;
    setDigits(next);
    if (val && idx < 5) inputRefs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      setDigits(text.split(''));
      inputRefs.current[5]?.focus();
      e.preventDefault();
    }
  };

  const code = digits.join('');

  const verify = async () => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      await api.post('/auth/verify-email', { email, code });
      toast.success('Почта подтверждена!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Неверный код');
    }
    setLoading(false);
  };

  const resend = async () => {
    setResending(true);
    try {
      await api.post('/auth/resend-verification', { email });
      toast.success('Код отправлен повторно');
      setCooldown(60);
    } catch {
      toast.error('Ошибка отправки');
    }
    setResending(false);
  };

  return (
    <div data-testid="verify-email-page" className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${AUTH_IMAGE})` }} />
      <div className="absolute inset-0" style={{ background: 'rgba(12, 10, 9, 0.88)', backdropFilter: 'blur(4px)' }} />

      <div className="relative z-10 w-full max-w-md mx-4 animate-fade-in">
        <div className="bg-stone-900/90 border border-stone-800 rounded-xl p-8 shadow-2xl shadow-black/60 backdrop-blur-sm">
          <Link to="/register" className="flex items-center gap-1 mb-6 text-stone-500 hover:text-stone-300 text-sm transition-colors">
            <ArrowLeft size={14} /> Назад
          </Link>

          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-900/20 flex items-center justify-center">
              <Mail size={28} className="text-amber-500" />
            </div>
            <h2 className="font-heading text-xl text-stone-200 mb-2">Подтвердите почту</h2>
            <p className="text-stone-500 text-sm">
              Мы отправили 6-значный код на<br />
              <span className="text-amber-500 font-medium">{email}</span>
            </p>
          </div>

          <div className="flex justify-center gap-2 mb-6" onPaste={handlePaste}>
            {digits.map((d, i) => (
              <Input
                key={i}
                data-testid={`verify-digit-${i}`}
                ref={el => inputRefs.current[i] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={d}
                onChange={e => handleChange(i, e.target.value)}
                onKeyDown={e => handleKeyDown(i, e)}
                className="w-12 h-14 text-center text-xl font-bold bg-stone-950/50 border-stone-800 focus:border-amber-700 text-stone-200"
                autoFocus={i === 0}
              />
            ))}
          </div>

          <Button
            data-testid="verify-submit-btn"
            onClick={verify}
            disabled={code.length !== 6 || loading}
            className={`w-full h-11 font-medium transition-all ${code.length === 6 ? 'bg-amber-700 hover:bg-amber-600 text-white' : 'bg-stone-800 text-stone-600 cursor-not-allowed'}`}
          >
            {loading ? 'Проверяю...' : 'Подтвердить'}
          </Button>

          <div className="text-center mt-4">
            <button
              data-testid="resend-code-btn"
              onClick={resend}
              disabled={resending || cooldown > 0}
              className="text-stone-500 hover:text-amber-500 text-sm transition-colors disabled:text-stone-700 disabled:cursor-not-allowed"
            >
              {cooldown > 0 ? `Отправить повторно (${cooldown}с)` : resending ? 'Отправляю...' : 'Отправить код повторно'}
            </button>
          </div>

          <p className="text-stone-700 text-xs text-center mt-4">Проверьте папку «Спам», если письмо не пришло</p>
        </div>
      </div>
    </div>
  );
}
